"use client";

import React from "react";
import {
  Box,
  Button,
  IconButton,
  Grid,
  useMediaQuery,
  Tooltip,
} from "@mui/material";

import { useTheme, styled } from "@mui/material/styles";
import { useRouter, usePathname } from "next/navigation";
import Image from "next/image";
import ThemeToggle from "@/app/components/dashboards/layout/header/ThemeToggle";
import PersonSearchIcon from "@mui/icons-material/PersonSearch";
import PersonIcon from "@mui/icons-material/Person";
import BubbleChart from "@mui/icons-material/BubbleChart";

const ProfileButton = styled(IconButton)(({ theme, selected }) => ({
  marginLeft: theme.spacing(1),
  transition: "all 0.3s ease",
  color: selected ? theme.palette.grey[600] : theme.palette.grey[500],
  backgroundColor: selected ? theme.palette.action.selected : "transparent",
  "&:hover": {
    backgroundColor: theme.palette.action.hover,
    color: theme.palette.grey[600],
  },
}));

const HeaderButton = styled(IconButton)(({ theme, selected }) => ({
  marginLeft: theme.spacing(1),
  transition: "all 0.3s ease",
  color: selected ? theme.palette.grey[600] : theme.palette.grey[500],
  backgroundColor: selected ? theme.palette.action.selected : "transparent",
  "&:hover": {
    backgroundColor: theme.palette.action.hover,
    color: theme.palette.grey[600],
  },
}));

export default function PublicHeader() {
  const router = useRouter();
  const pathname = usePathname();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const handleLoginClick = () => {
    router.push("/");
  };

  const handleProfileClick = () => {
    router.push("/");
  };

  const handleSearchClick = () => {
    router.push("/search");
  };

  const handleSupportClick = () => {
    router.push("/support");
  };

  const handleLogoClick = () => {
    window.open("https://meex.co", "_blank", "noopener,noreferrer");
  };

  const isSearchPage = pathname === "/search";
  const isSupportPage = pathname === "/support";
  const isProfilePage = pathname === "/";

  return (
    <Grid
      container
      sx={{
        alignItems: "center",
        justifyContent: "space-between",
        padding: theme.spacing(2, 0),
        marginBottom: theme.spacing(0),
        marginTop: theme.spacing(1),
        pl: 3.5,
        pr: { xs: 1.5, sm: 3.5 },
      }}
    >
      <Box onClick={handleLogoClick} sx={{ cursor: "pointer" }}>
        <Image
          src={
            theme.palette.mode === "dark"
              ? "/images/logos/light-logo.svg"
              : "/images/logos/dark-logo.svg"
          }
          alt="Meex Logo"
          width={40}
          height={40}
          priority
        />
      </Box>

      <Box>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 0.25,
          }}
        >
          {isSearchPage ? (
            <Tooltip
              title="Everything You Need to Know"
              arrow
              placement="bottom"
            >
              <HeaderButton
                onClick={handleSupportClick}
                selected={isSupportPage}
              >
                <BubbleChart sx={{ fontSize: 30 }} />
              </HeaderButton>
            </Tooltip>
          ) : (
            <Tooltip title="Search Users" arrow placement="bottom">
              <HeaderButton onClick={handleSearchClick} selected={isSearchPage}>
                <PersonSearchIcon sx={{ fontSize: 30 }} />
              </HeaderButton>
            </Tooltip>
          )}
          <ThemeToggle />
          {isMobile ? (
            <Tooltip title="Sign in" arrow placement="bottom">
              <ProfileButton
                onClick={handleProfileClick}
                selected={isProfilePage}
                sx={{
                  backgroundColor: "transparent!important",
                  "&:hover": {
                    backgroundColor: "transparent!important",
                  },
                  "&.Mui-focusVisible": {
                    backgroundColor: "transparent!important",
                  },
                }}
              >
                <PersonIcon sx={{ fontSize: 30 }} />
              </ProfileButton>
            </Tooltip>
          ) : null}
          {isMobile ? null : <Box sx={{ ml: 1 }} />}
          {isMobile ? null : (
            <Button
              variant="contained"
              onClick={handleLoginClick}
              sx={{
                backgroundColor: theme.palette.grey[600],
                color: theme.palette.grey[100],
                "&:hover": {
                  backgroundColor: theme.palette.grey[500],
                },
                width: { xs: "100px", sm: "150px" },
                ml: 1,
                borderRadius: "25px!important",
              }}
            >
              Sign in
            </Button>
          )}
        </Box>
      </Box>
    </Grid>
  );
}
